import { useCallback } from 'preact/hooks'
import { ChangeEvent } from 'preact/compat'
import { Segment, Form, Grid, CheckboxProps } from 'semantic-ui-react'
import { Text } from 'preact-i18n'

import { ScanditRadio, SvgImg, Switch, ScrollableSegment } from '../../atoms'

import OneDIndustrialImage from '../assets/1d_industrial.svg'
import OneDRetailImage from '../assets/1d_retail.svg'
import TwoDAllImage from '../assets/2d_all.svg'
import AllImage from '../assets/all.svg'
import DpmImage from '../assets/dpm.svg'
import QRImage from '../assets/qr.svg'
import BatchImage from '../assets/batch.svg'
import ListBuildingImage from '../assets/list_building.svg'
import LabelScanningImage from '../assets/label_scanning.svg'
import SearchAndFindimage from '../assets/search_and_find.svg'

import type { ScannerMode } from '../../../store/InitialOptionsStore'

type StartedOptions = { scannerMode: ScannerMode }

type IntroFormProps = {
  onScannerModeChange: (selection: StartedOptions) => void
  onColorInvertedChange: (data: { colorInverted: boolean }) => void
  scannerMode: ScannerMode | undefined
  colorInvertedValue: boolean
  colorInvertedState: boolean
}

const IntroForm = ({
  onScannerModeChange,
  onColorInvertedChange,
  scannerMode,
  colorInvertedValue,
  colorInvertedState,
}: IntroFormProps) => {
  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const target = event.target as HTMLInputElement
      const _scannerMode = target.value as ScannerMode
      onScannerModeChange({ scannerMode: _scannerMode })
    },
    [onScannerModeChange]
  )
  return (
    <>
      <ScrollableSegment>
        <Form inverted>
          <Grid stackable columns={2}>
            <Grid.Row>
              <Grid.Column>
                <ScanditRadio
                  id="radio-scannerMode-1dRetail"
                  name="scannerMode"
                  value="1dRetail"
                  title={<Text id="scannerModePresets.1dRetail" />}
                  image={<SvgImg src={OneDRetailImage} alt="1dRetail" />}
                  description="EAN-13, EAN-8, EAN-2 & EAN-5 Addons, UPC, DataBar"
                  checked={scannerMode === '1dRetail'}
                  onChange={onChange}
                />
              </Grid.Column>
              <Grid.Column>
                <ScanditRadio
                  id="radio-scannerMode-qr"
                  name="scannerMode"
                  value="qr"
                  title={<Text id="scannerModePresets.qr" />}
                  image={<SvgImg src={QRImage} alt="qr" />}
                  checked={scannerMode === 'qr'}
                  onChange={onChange}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <ScanditRadio
                  id="radio-scannerMode-1dIndustrial"
                  name="scannerMode"
                  value="1dIndustrial"
                  title={<Text id="scannerModePresets.1dIndustrial" />}
                  image={<SvgImg src={OneDIndustrialImage} alt="1dIndustrial" />}
                  description={'Code 128, Code 39, Code 93, ITF, Code 11, Code 25, Codabar, MSI-Plessey'}
                  checked={scannerMode === '1dIndustrial'}
                  onChange={onChange}
                />
              </Grid.Column>
              <Grid.Column>
                <ScanditRadio
                  id="radio-scannerMode-dpm"
                  name="scannerMode"
                  value="dpm"
                  title={<Text id="scannerModePresets.dpm" />}
                  image={<SvgImg src={DpmImage} alt="dpm" />}
                  checked={scannerMode === 'dpm'}
                  onChange={onChange}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <ScanditRadio
                  id="radio-scannerMode-2dAll"
                  name="scannerMode"
                  value="2dAll"
                  title={<Text id="scannerModePresets.all2d" />}
                  image={<SvgImg src={TwoDAllImage} alt="2dAll" />}
                  checked={scannerMode === '2dAll'}
                  onChange={onChange}
                />
              </Grid.Column>
              <Grid.Column>
                <ScanditRadio
                  id="radio-scannerMode-all"
                  name="scannerMode"
                  value="all"
                  title={<Text id="scannerModePresets.all" />}
                  image={<SvgImg src={AllImage} alt={<Text id="scannerModePresets.all" />} />}
                  checked={scannerMode === 'all'}
                  onChange={onChange}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <ScanditRadio
                  id="radio-scannerMode-labelScanning"
                  name="scannerMode"
                  value="labelScanning"
                  title={<Text id="scannerModePresets.labelScanning" />}
                  description={<Text id="scannerModePresets.labelScanningDescription" />}
                  image={
                    <SvgImg
                      src={LabelScanningImage}
                      alt={<Text id="scannerModePresets.labelScanning" />}
                      style={{ padding: '0.5rem' }}
                    />
                  }
                  checked={scannerMode === 'labelScanning'}
                  onChange={onChange}
                />
              </Grid.Column>
              <Grid.Column>
                <ScanditRadio
                  id="radio-scannerMode-batch"
                  name="scannerMode"
                  value="batch"
                  title={<Text id="scannerModePresets.batch" />}
                  description={<Text id="scannerModePresets.batchDescription" />}
                  image={
                    <SvgImg
                      src={BatchImage}
                      alt={<Text id="scannerModePresets.batch" />}
                      style={{ padding: '0.5rem' }}
                    />
                  }
                  checked={scannerMode === 'batch'}
                  onChange={onChange}
                />
              </Grid.Column>
              <Grid.Column>
                <ScanditRadio
                  id="radio-scannerMode-externalListBuilding"
                  name="scannerMode"
                  value="externalListBuilding"
                  title={<Text id="scannerModePresets.externalListBuilding" />}
                  description={<Text id="scannerModePresets.externalListBuildingDescription" />}
                  image={
                    <SvgImg
                      src={ListBuildingImage}
                      alt={<Text id="scannerModePresets.externalListBuilding" />}
                      style={{ padding: '0.5rem' }}
                    />
                  }
                  checked={scannerMode === 'externalListBuilding'}
                  onChange={onChange}
                />
              </Grid.Column>
              <Grid.Column>
                <ScanditRadio
                  id="radio-scannerMode-externalSearchAndFind"
                  name="scannerMode"
                  value="externalSearchAndFind"
                  title={<Text id="scannerModePresets.externalSearchAndFind" />}
                  description={<Text id="scannerModePresets.externalSearchAndFindDescription" />}
                  image={
                    <SvgImg
                      src={SearchAndFindimage}
                      alt={<Text id="scannerModePresets.externalSearchAndFind" />}
                      style={{ padding: '0.5rem' }}
                    />
                  }
                  checked={scannerMode === 'externalSearchAndFind'}
                  onChange={onChange}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </ScrollableSegment>
      <Segment inverted textAlign="center">
        <Switch
          label={
            <label htmlFor="colorInverted">
              <Text id="options.colorInverted" />
            </label>
          }
          id="colorInverted"
          disabled={!colorInvertedState}
          checked={colorInvertedValue}
          onChange={(e: ChangeEvent<HTMLInputElement>, data: CheckboxProps) => {
            const checked = (e.target as HTMLInputElement).checked
            onColorInvertedChange({ colorInverted: checked })
          }}
        />
      </Segment>
    </>
  )
}

export default IntroForm
